/**
 * ElectronImportPanel — Main orchestrator for Electron import flow
 *
 * Manages the 3-step flow:
 * 1. ImportBrowser — select folder/files, browse, select items
 * 2. Detection — detect formats via ConversationParser
 * 3. Import — run import, show progress
 *
 * @module @humanizer/studio/components/import/ElectronImportPanel
 */

import React, { useState, useCallback, useEffect, useRef } from 'react';
import { ImportBrowser } from './ImportBrowser';
import type { ImportProgress } from '../../types/electron';
import { createLogger } from '../../utils/debug';

const log = createLogger('Import');

interface ElectronImportPanelProps {
  onImportComplete?: () => void;
  className?: string;
}

type Step = 'browse' | 'detect' | 'importing' | 'results';

/** One detected source ready for import */
interface DetectedSource {
  path: string;
  format: string;
  confidence: number;
  reason: string;
  conversationCount: number;
}

interface ImportResultSummary {
  imported: number;
  skipped: number;
  failed: number;
  formats: string[];
  conversations: number;
}

const FORMAT_LABELS: Record<string, string> = {
  openai: 'ChatGPT / OpenAI',
  claude: 'Claude',
  facebook: 'Facebook',
  'chrome-plugin': 'Browser Plugin',
  reddit: 'Reddit',
  twitter: 'Twitter / X',
  instagram: 'Instagram',
  substack: 'Substack',
  text: 'Text Document',
  markdown: 'Markdown Document',
  pdf: 'PDF Document',
  latex: 'LaTeX Document',
  'text-collection': 'Text Document Collection',
  'image-collection': 'Image Collection',
  unknown: 'Unknown Format',
};

export function ElectronImportPanel({
  onImportComplete,
  className = '',
}: ElectronImportPanelProps): React.ReactElement {
  const [step, setStep] = useState<Step>('browse');
  const [selectedPaths, setSelectedPaths] = useState<Set<string>>(new Set());
  const [detections, setDetections] = useState<DetectedSource[]>([]);
  const [detecting, setDetecting] = useState(false);
  const [detectError, setDetectError] = useState<string | null>(null);
  const [progress, setProgress] = useState<ImportProgress | null>(null);
  const [results, setResults] = useState<ImportResultSummary | null>(null);
  const [importError, setImportError] = useState<string | null>(null);

  const [embedState, setEmbedState] = useState<'idle' | 'running' | 'done'>('idle');
  const [embedCount, setEmbedCount] = useState(0);
  const unsubProgress = useRef<(() => void) | null>(null);
  const unsubEmbed = useRef<(() => void) | null>(null);

  // Cleanup listeners
  useEffect(() => {
    return () => {
      unsubProgress.current?.();
      unsubEmbed.current?.();
    };
  }, []);

  // Shared detection logic — runs detect on each path
  const runDetection = useCallback(async (paths: string[]) => {
    const api = window.electronImport;
    if (!api || paths.length === 0) return;

    setDetecting(true);
    setDetectError(null);
    setStep('detect');

    try {
      const sources: DetectedSource[] = [];

      for (const p of paths) {
        try {
          const matches = await api.detect(p);
          if (matches.length > 0) {
            const best = matches[0];
            sources.push({
              path: p,
              format: best.format,
              confidence: best.confidence,
              reason: best.reason || '',
              conversationCount: best.stats?.conversationCount || 0,
            });
          }
        } catch {
          // Skip paths that fail detection
        }
      }

      setDetections(sources.sort((a, b) => b.confidence - a.confidence));
    } catch (err) {
      setDetectError(err instanceof Error ? err.message : 'Detection failed');
    } finally {
      setDetecting(false);
    }
  }, []);

  // Detect selected files from browser
  const handleDetect = useCallback(() => {
    runDetection(Array.from(selectedPaths));
  }, [selectedPaths, runDetection]);

  // Detect a whole folder
  const handleDetectFolder = useCallback((folderPath: string) => {
    runDetection([folderPath]);
  }, [runDetection]);

  // Detect files selected via file picker (bypass browser)
  const handleDetectFiles = useCallback((filePaths: string[]) => {
    runDetection(filePaths);
  }, [runDetection]);

  // Run import for all detected sources
  const handleImport = useCallback(async () => {
    const api = window.electronImport;
    if (!api) return;

    setStep('importing');
    setImportError(null);
    setProgress({ phase: 'Starting...', percent: 0, imported: 0, skipped: 0 });

    // Subscribe to progress events
    unsubProgress.current?.();
    unsubProgress.current = api.onProgress((data) => {
      setProgress(data);
    });

    let totalImported = 0, totalSkipped = 0, totalFailed = 0, totalConvs = 0;
    const formatSet = new Set<string>();

    try {
      const textFormats = ['text', 'markdown', 'pdf', 'latex', 'text-collection'];
      const imageFormats = ['image-collection'];

      for (const source of detections) {
        if (source.format === 'unknown' && source.confidence < 0.3) continue;

        try {
          const result = imageFormats.includes(source.format)
            ? await api.executeImages({ path: source.path })
            : textFormats.includes(source.format)
            ? await api.executeText({ path: source.path })
            : await api.execute({ path: source.path });
          totalImported += result.imported;
          totalSkipped += result.skipped;
          totalFailed += result.failed;
          totalConvs += result.conversations;
          formatSet.add(source.format);
        } catch (err) {
          totalFailed++;
          log.error('Failed for path:', source.path, err);
        }
      }

      setResults({
        imported: totalImported,
        skipped: totalSkipped,
        failed: totalFailed,
        formats: Array.from(formatSet),
        conversations: totalConvs,
      });
      setStep('results');
    } catch (err) {
      setImportError(err instanceof Error ? err.message : 'Import failed');
      setStep('detect');
    } finally {
      unsubProgress.current?.();
      unsubProgress.current = null;
    }
  }, [detections]);

  // Embed imported content
  const handleEmbed = useCallback(async () => {
    const archive = window.electronArchive;
    if (!archive?.embedBatch || !archive?.onEmbedProgress) return;

    setEmbedState('running');
    unsubEmbed.current = archive.onEmbedProgress((p) => {
      setEmbedCount(p.embedded);
    });

    try {
      await archive.embedBatch({ batchSize: 500 });
    } catch {
      // graceful degradation
    } finally {
      setEmbedState('done');
      unsubEmbed.current?.();
      unsubEmbed.current = null;
    }
  }, []);

  // Run repair media for detected sources (fixes broken images on existing imports)
  const handleRepairMedia = useCallback(async () => {
    const api = window.electronImport;
    if (!api) return;

    setStep('importing');
    setImportError(null);
    setProgress({ phase: 'Starting media repair...', percent: 0, imported: 0, skipped: 0 });

    unsubProgress.current?.();
    unsubProgress.current = api.onProgress((data) => {
      setProgress(data);
    });

    let totalUpdated = 0, totalCopied = 0;
    const formatSet = new Set<string>();

    try {
      for (const source of detections) {
        try {
          const result = await api.repairMedia({ path: source.path });
          totalUpdated += result.nodesUpdated;
          totalCopied += result.mediaFilesCopied;
          formatSet.add(result.format);
        } catch (err) {
          log.error('Repair media failed for path:', source.path, err);
        }
      }

      setResults({
        imported: totalUpdated,
        skipped: totalCopied,
        failed: 0,
        formats: Array.from(formatSet),
        conversations: 0,
      });
      setStep('results');
    } catch (err) {
      setImportError(err instanceof Error ? err.message : 'Media repair failed');
      setStep('detect');
    } finally {
      unsubProgress.current?.();
      unsubProgress.current = null;
    }
  }, [detections]);

  // Reset to browse step
  const handleReset = useCallback(() => {
    setStep('browse');
    setSelectedPaths(new Set());
    setDetections([]);
    setDetectError(null);
    setImportError(null);
    setResults(null);
    setProgress(null);
    setEmbedState('idle');
    setEmbedCount(0);
  }, []);

  return (
    <div className={`electron-import ${className}`}>
      {step === 'browse' && (
        <ImportBrowser
          selectedPaths={selectedPaths}
          onSelectionChange={setSelectedPaths}
          onDetect={handleDetect}
          onDetectFolder={handleDetectFolder}
          onDetectFiles={handleDetectFiles}
        />
      )}

      {step === 'detect' && (
        <div className="electron-import__detect">
          <div className="electron-import__header">
            <button
              className="import-wizard__btn import-wizard__btn--secondary"
              onClick={() => setStep('browse')}
            >
              Back
            </button>
            <h3 className="electron-import__title">Detected Sources</h3>
          </div>

          {detecting ? (
            <div className="electron-import__loading">
              <div className="panel__loading-spinner" />
              <span>Detecting importable content...</span>
            </div>
          ) : detectError ? (
            <div className="import-browser__error">{detectError}</div>
          ) : detections.length === 0 ? (
            <div className="electron-import__empty">
              <p>No recognized formats found in the selected files.</p>
              <p className="electron-import__hint">
                Try selecting a folder containing exported data from ChatGPT, Claude,
                Facebook, Twitter, Reddit, or other supported platforms.
              </p>
            </div>
          ) : (
            <>
              <div className="electron-import__groups">
                {detections.map((source) => (
                  <div key={source.path} className="electron-import__group">
                    <div className="electron-import__group-header">
                      <span className="electron-import__group-name">
                        {FORMAT_LABELS[source.format] || source.format}
                      </span>
                      <span className="electron-import__group-confidence">
                        {Math.round(source.confidence * 100)}% match
                      </span>
                    </div>
                    <div className="electron-import__group-detail">
                      {source.reason}
                    </div>
                    <div className="electron-import__group-path">
                      {source.path.split('/').slice(-2).join('/')}
                    </div>
                  </div>
                ))}
              </div>

              {importError && (
                <div className="import-browser__error">{importError}</div>
              )}

              <div className="electron-import__actions">
                <button
                  className="import-wizard__btn import-wizard__btn--secondary"
                  onClick={handleRepairMedia}
                  title="Fix broken images for already-imported archives"
                >
                  Repair Media
                </button>
                <button
                  className="import-wizard__btn import-wizard__btn--primary"
                  onClick={handleImport}
                >
                  Import {detections.length} Source{detections.length !== 1 ? 's' : ''}
                </button>
              </div>
            </>
          )}
        </div>
      )}

      {step === 'importing' && (
        <div className="electron-import__progress" aria-live="polite" role="status">
          <div className="panel__loading-spinner" />
          <h3 className="electron-import__title">Importing...</h3>
          {progress && (
            <div className="electron-import__progress-detail">
              <span className="electron-import__progress-phase">{progress.phase}</span>
              <div className="electron-import__progress-counts">
                <span>{progress.imported} imported</span>
                {progress.skipped > 0 && <span>{progress.skipped} skipped</span>}
              </div>
              {progress.percent != null && progress.percent > 0 && (
                <div className="electron-import__progress-bar">
                  <div
                    className="electron-import__progress-fill"
                    style={{ width: `${Math.min(100, progress.percent)}%` }}
                  />
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {step === 'results' && results && (
        <div className="electron-import__results">
          <div className="electron-import__results-icon">
            {results.failed === 0 ? '✓' : '⚠'}
          </div>
          <h3 className="electron-import__title">Import Complete</h3>
          <div className="electron-import__results-summary">
            <div className="electron-import__results-stat">
              <span className="electron-import__results-value">{results.imported}</span>
              <span className="electron-import__results-label">Imported</span>
            </div>
            {results.conversations > 0 && (
              <div className="electron-import__results-stat">
                <span className="electron-import__results-value">{results.conversations}</span>
                <span className="electron-import__results-label">Conversations</span>
              </div>
            )}
            {results.skipped > 0 && (
              <div className="electron-import__results-stat">
                <span className="electron-import__results-value">{results.skipped}</span>
                <span className="electron-import__results-label">Skipped</span>
              </div>
            )}
            {results.failed > 0 && (
              <div className="electron-import__results-stat">
                <span className="electron-import__results-value">{results.failed}</span>
                <span className="electron-import__results-label">Failed</span>
              </div>
            )}
          </div>
          {results.formats.length > 0 && (
            <p className="electron-import__results-adapters">
              Sources: {results.formats.map(f => FORMAT_LABELS[f] || f).join(', ')}
            </p>
          )}
          {results.imported > 0 && window.electronArchive?.embedBatch && embedState === 'idle' && (
            <button
              className="import-wizard__btn import-wizard__btn--secondary electron-import__embed-btn"
              onClick={handleEmbed}
            >
              Embed imported content for search
            </button>
          )}
          {embedState === 'running' && (
            <div className="electron-import__embed-progress">
              <div className="panel__loading-spinner" />
              <span>Embedding... {embedCount.toLocaleString()} nodes</span>
            </div>
          )}
          {embedState === 'done' && (
            <div className="electron-import__embed-done">
              Embedded {embedCount.toLocaleString()} nodes — content is now searchable.
            </div>
          )}
          <div className="electron-import__actions">
            <button
              className="import-wizard__btn import-wizard__btn--secondary"
              onClick={handleReset}
            >
              Import More
            </button>
            <button
              className="import-wizard__btn import-wizard__btn--primary"
              onClick={() => onImportComplete?.()}
            >
              Done
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

export default ElectronImportPanel;
